import React, { useState, useEffect, useRef } from "react";
import DateConverter from "@heartinz/date_and_time_processor";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { CircularProgress } from "@mui/material";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { useAuth } from "../../../context/AuthProvider";
import { Link } from "react-router-dom";
import GetServerLiveTime from "../../../constants/GetServerLiveTime";
import InvoiceTempPreview from "../settings/Invoice/InvoiceTempPreview";
import { usePermission } from "../../../context/PermissionContext";
import ViewServiceDoneByReport from "./ViewServiceDonebyRebort";
import TemplateModalLayout from "../../../templates/modules/TemplateModalLayout";
import { ChevronRightIcon, DownloadIcon, SpreadSheetIcon } from "../../../constants/Icons";

const ServiceDoneByReport = ({ isActive }) => {
  const [errors, setErrors] = useState("");
  const { isUser, updateSettings } = useAuth();
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const serverTime = GetServerLiveTime();
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const suggestionRefs = useRef([]);
  const wrapperRef = useRef(null);
  const { storeSettings } = usePermission();

  var obj = {
    label: isUser.title,
    value: isUser.storeId,
    stype: isUser.stype,
  };

  const [isPreLoading, setPreLoading] = useState(true);
  const [isbtnLoading, setButtonLoading] = useState(false);
  const [saleMode, setSaleMode] = useState("");

  const [options, setOptions] = useState([]);
  const [isSelectedStore, setSelectedStore] = useState(obj);
  const storeSelectedRef = useRef(null);

  const [isProductReportData, setProductReportData] = useState([]);

  const [viewData, setViewData] = useState(null);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isViewTempModalOpen, setViewTempModalOpen] = useState(false);

  const [selectedOption, setSelectedOption] = useState("");
  const [selectTermalOption, setSelectTermalOption] = useState("");

  const templateId = storeSettings?.invoiceSettings?.template?.templateId;

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const currentDate = new Date();
  const previousDate = new Date(serverTime.getTime() - 1000);
  const [fromDate, setFromDate] = useState(previousDate);
  const [toDate, setToDate] = useState(serverTime);

  const calculateDate = (baseDate, offsetDays) => {
    const resultDate = new Date(baseDate);
    resultDate.setDate(resultDate.getDate() + offsetDays);
    return resultDate;
  };

  const handleFromDateChange = (newValue) => {
    if (!newValue) return;
    const selectedFrom = newValue.startOf("day");

    const maxAllowedTo = selectedFrom.add(64, "days");
    const finalMaxTo = maxAllowedTo.isAfter(dayjs(serverTime))
      ? dayjs(serverTime)
      : maxAllowedTo;

    setFromDate(selectedFrom.toDate());
    if (
      dayjs(toDate).isBefore(selectedFrom) ||
      dayjs(toDate).isAfter(finalMaxTo)
    ) {
      setToDate(finalMaxTo.toDate());
    }
  };

  const handleToDateChange = (newValue) => {
    if (!newValue) return;
    const selectedTo = newValue.endOf("day");

    if (selectedTo.isBefore(dayjs(fromDate))) {
      setFromDate(selectedTo.startOf("day").toDate());
    }
    setToDate(selectedTo.toDate());
  };

  const toDateMin = fromDate;
  const toDateMax = dayjs(fromDate).add(64, "day").isAfter(dayjs(serverTime))
    ? dayjs(serverTime)
    : dayjs(fromDate).add(64, "day");

  const handleListStore = async () => {
    setPreLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`list-store`);
      const options = formatOptions(response.data.results, isUser.stype);
      setOptions(options);
      const userStore = options.find(
        (option) => option.value === isUser.storeId,
      );
      setSelectedStore(userStore);
      handleGenerateReport();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPreLoading(false);
    }
  };

  const formatOptions = (data, userType) => {
    if (userType === "MAIN") {
      const mainOption = {
        label: data.title,
        value: data._id,
        stype: "MAIN",
      };

      const subOptions = data.subs.map((subStore) => ({
        label: subStore.title,
        value: subStore._id,
        stype: "SUB",
      }));

      const hubOptions = data.subs.reduce((acc, subStore) => {
        if (subStore.hubs && subStore.hubs.length > 0) {
          const hubs = subStore.hubs.map((hub) => ({
            label: hub.title,
            value: hub._id,
            stype: "HUB",
          }));
          return acc.concat(hubs);
        }
        return acc;
      }, []);

      return [mainOption, ...subOptions, ...hubOptions];
    }

    if (userType === "SUB") {
      const subOption = {
        label: data.title,
        value: data._id,
        stype: "SUB",
      };

      let hubOptions = [];
      if (data.hubs && data.hubs.length > 0) {
        hubOptions = data.hubs.map((hub) => ({
          label: hub.title,
          value: hub._id,
          stype: "HUB",
        }));
      }

      return [subOption, ...hubOptions];
    }

    if (userType == "HUB") {
      const hubOptions = {
        label: data.title,
        value: data._id,
        stype: "HUB",
      };
      return [hubOptions];
    }
    return [];
  };

  const getColor = (status) => {
    switch (status.toLowerCase()) {
      case "main":
        return "bg-label-orange";
      case "sub":
        return "bg-label-peach";
      case "hub":
        return "bg-label-night";
      default:
        return "bg-label-secondary";
    }
  };

  const renderOptionLabel = ({ label, stype }) => {
    let marginRight;
    let fontWeight;
    let badgePadding;
    let fontSize;

    if (stype === "MAIN") {
      marginRight = "0px";
      fontWeight = "500";
      fontSize = "14.5px";
      badgePadding = "2px 8px";
    } else if (stype === "SUB") {
      marginRight = "2px";
      fontWeight = "400";
      fontSize = "14px";
      badgePadding = "2px 12px";
    } else if (stype === "HUB") {
      marginRight = "4px";
      fontWeight = "300";
      fontSize = "13.5px";
      badgePadding = "2px 14px";
    }

    return (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          marginLeft: marginRight,
        }}
      >
        <span
          className={`badge rounded-pill ${getColor(stype)}`}
          style={{
            marginRight: "5px",
            padding: badgePadding,
          }}
        >
          {stype}
        </span>
        <span style={{ fontWeight, fontSize }}>- {label}</span>
      </span>
    );
  };

  const handleSelectStore = (selected) => {
    setSelectedStore(selected);
  };

  const validateForm = () => {
    const errors = {};
    if (!isSelectedStore) {
      errors.store = Messages.ERROR.EMPTY_STORE;
    }
    setErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      switch (firstErrorField) {
        case "store":
          storeSelectedRef.current?.focus();
          break;
        default:
          break;
      }
    }
    return Object.keys(errors).length === 0;
  };

  const handleProductSearch = (term) => {
    setProductSearchTerm(term);

    if (term.trim() === "") {
      setProductSuggestions([]);
      setShowProductDropdown(false);
      setSelectedProduct(null);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        setPreLoading(true);

        controllerRef.current = new AbortController();
        const currentId = ++requestIdRef.current;

        const response = await AxiosInstance.get(
          `module/product/search-hybrid-active?searchtxt=${term}`,
          { signal: controllerRef.current.signal },
        );

        if (currentId === requestIdRef.current) {
          const results = response.data.results || [];
          setProductSuggestions(results);
          setShowProductDropdown(results.length > 0);
        }
      } catch (error) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          console.error("API Error:", error);
        }
      } finally {
        setPreLoading(false);
      }
    }, 200);
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setProductSearchTerm(product.title);
    setShowProductDropdown(false);
  };

  const handleGenerateReport = async () => {
    if (!validateForm()) return;
    setPreLoading(true);
    setButtonLoading(true);
    try {
      if (!checkNetwork()) return;

      const fromDateParam = encodeURIComponent(
        DateConverter.convertToTimeZone(
          fromDate.toISOString(),
          isUser.timezone,
        ),
      );
      const toDateParam = encodeURIComponent(
        DateConverter.convertToTimeZone(toDate.toISOString(), isUser.timezone),
      );

      const response = await AxiosInstance.get(
        `module/reports/service-doneby?fromdate=${fromDateParam}&todate=${toDateParam}`,
      );
      setProductReportData(response.data.results);
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setPreLoading(false);
      setButtonLoading(false);
    }
  };

  const handleProductKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, productSuggestions.length - 1),
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();

      if (highlightedIndex >= 0) {
        const selected = productSuggestions[highlightedIndex];
        handleSelectProduct(selected);
        setShowProductDropdown(false);
        setHighlightedIndex(-1);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const exportToCSV = () => {
    const reportArray = isProductReportData?.reportData || [];
    if (!Array.isArray(reportArray) || reportArray.length === 0) {
      showAlert("warning", "No data available to export");
      return;
    }
    const clean = (val) => {
      if (val == null) return "";
      let str = val.toString().replace(/[\r\n;]/g, " ");
      if (str.includes(",") || str.includes('"')) {
        str = `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    let csvContent = "";
    const headers =
      "S.No,Sale Mode,Sale Date,Sale No,Service Title,MRP,Discount,Amount,Tax,Qty,Total\n";

    csvContent += headers;

    reportArray.forEach((serviceGroup) => {
      // Service Done By Heading
      csvContent += `\n${clean(serviceGroup.serviceDoneBy)}\n`;

      serviceGroup.sales.forEach((sale, index) => {
        csvContent +=
          [
            index + 1,
            clean(sale?.saleMode),
            clean(
              DateConverter.ISOToDisplayFormat(
                DateConverter.convertToTimeZone(sale.saleDate, isUser.timezone),
              ).date,
              DateConverter.ISOToDisplayFormat(
                DateConverter.convertToTimeZone(sale.saleDate, isUser.timezone),
              ).time,
            ),
            clean(sale?.saleNo),
            clean(sale.serviceItem.title),
            clean(
              CurrencyConverter.toCurrency(
                sale?.serviceItem.lineTotalMrp || 0,
                "INR",
              ),
            ),
            clean(
              CurrencyConverter.toCurrency(
                sale?.serviceItem.lineTotalDiscount || 0,
                "INR",
              ),
            ),
            clean(
              CurrencyConverter.toCurrency(
                sale?.serviceItem.lineTotalBasePrice || 0,
                "INR",
              ),
            ),
            clean(
              CurrencyConverter.toCurrency(
                sale?.serviceItem.lineTotalTaxAmount || 0,
                "INR",
              ),
            ),
            clean(
              sale?.serviceItem.quantity,
              sale?.serviceItem.unitConfig?.primaryUnitCode,
            ),
            clean(
              CurrencyConverter.toCurrency(
                sale?.serviceItem.lineGrandTotal || 0,
                "INR",
              ),
            ),
          ].join(",") + "\n";
      });

      // Group Total
      csvContent +=
        [
          "",
          "",
          "",
          "",
          "TOTAL",
          clean(
            CurrencyConverter.toCurrency(
              serviceGroup?.summary?.totalMrp || 0,
              "INR",
            ),
          ),
          clean(
            CurrencyConverter.toCurrency(
              serviceGroup?.summary?.totalDiscount || 0,
              "INR",
            ),
          ),
          clean(
            CurrencyConverter.toCurrency(
              serviceGroup?.summary?.totalBasePrice || 0,
              "INR",
            ),
          ),
          clean(
            CurrencyConverter.toCurrency(
              serviceGroup?.summary?.totalTaxAmount || 0,
              "INR",
            ),
          ),
          "",
          clean(
            CurrencyConverter.toCurrency(
              serviceGroup?.summary?.grandTotal || 0,
              "INR",
            ),
          ),
        ].join(",") + "\n\n";
    });
    csvContent += "\n\nSERVICE DONE BY SUMMARY\n";
    csvContent += "Service Done By,Total Services,Amount,Tax,Total Amount\n";

    reportArray.forEach((item) => {
      csvContent +=
        [
          clean(item?.serviceDoneBy),
          clean(item?.summary?.totalServices || 0),
          clean(item?.summary?.totalBasePrice || 0),
          clean(item?.summary?.totalTaxAmount || 0),
          clean(
            CurrencyConverter.toCurrency(item?.summary?.grandTotal || 0, "INR"),
          ),
        ].join(",") + "\n";
    });

    csvContent +=
      [
        "TOTAL",
        clean(
          reportArray.reduce(
            (sum, item) => sum + (item?.summary?.totalServices || 0),
            0,
          ),
        ),
        clean(
          CurrencyConverter.toCurrency(
            reportArray.reduce(
              (sum, item) => sum + (item?.summary?.totalBasePrice || 0),
              0,
            ),
            "INR",
          ),
        ),
        clean(
          CurrencyConverter.toCurrency(
            reportArray.reduce(
              (sum, item) => sum + (item?.summary?.totalTaxAmount || 0),
              0,
            ),
            "INR",
          ),
        ),
        clean(
          CurrencyConverter.toCurrency(
            reportArray.reduce(
              (sum, item) => sum + (item?.summary?.grandTotal || 0),
              0,
            ),
            "INR",
          ),
        ),
      ].join(",") + "\n";
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const from = formatDate(fromDate);
    const to = formatDate(toDate);

    link.setAttribute("href", url);
    link.setAttribute("download", `ServiceDoneBy_Report__${from}_to_${to}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = async () => {
    setSelectedStore("");
  };

  const handleSaleItems = async (saleId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`module/sale/${saleId}`);
      const results = response.data.results;
      return results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const handleSaleView = async (item) => {
    const data = await handleSaleItems(item.saleId);

    if (data) {
      setViewData(data);
      setViewModalOpen(true);
      setViewTempModalOpen(true);
    }
  };

  const handleSetTemplate = async (
    templateId,
    selectTermalOption,
    selectHalfA4Option,
    templateTheme,
    show = true,
  ) => {
    if (!checkNetwork()) return;

    try {
      const response = await AxiosInstance.patch(
        `module/storesettings/template`,
        {
          templateIdtemplateId: `${templateId}AND${selectTermalOption}AND${selectHalfA4Option}`,
          templateTheme,
        },
      );

      const results = response.data?.results?.invoiceSettings?.template;

      const id = results.templateId;
      const words = id.split("AND");

      setSelectedOption(words[0]);
      setSelectTermalOption(words[1]);

      setViewTempModalOpen(false);

      updateSettings({
        storeSettings: {
          invoiceSettings: {
            template: {
              templateId: results?.templateId,
              templateTheme: results?.templateTheme,
            },
            signature: results?.signature,
            termsAndConditions: results?.termsAndConditions,
          },
        },
      });

      if (show) {
        showAlert("success", "Template Selected...");
      }
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const handleSaleClose = () => {
    setViewModalOpen(false);
    setViewData(null);
  };

  useEffect(() => {
    handleListStore();
  }, []);

  useEffect(() => {
    const id = storeSettings?.invoiceSettings?.template?.templateId || "";
    const words = id ? id.split("AND") : [];

    setSelectedOption(words[0] || "");
    setSelectTermalOption(words[1] || "");
  }, [storeSettings]);

  return (
    <div>
      <div className="page-wrapper">
        <div className="page-content">
          <div className="page-breadcrumb d-flex align-items-center paddingBottom">
            <div className="breadcrumb-title pe-3">Service Done By Report</div>
            <div className="ms-auto">
              <div className="dropdown">
                <button
                  className="btn btn-sm btn-export dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <DownloadIcon/> Export
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <Link className="dropdown-item" onClick={exportToCSV}>
                      <SpreadSheetIcon/>{" "}
                      <span className="icon-text">Excel (CSV)</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-2">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3">
                    {/* <div
                      className="col-12"
                      ref={wrapperRef}
                      style={{ position: "relative" }}
                    >
                      <label className="form-label w-100">Select Product</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          name="search"
                          ref={suggestionsRef}
                          autoComplete="off"
                          value={productSearchTerm}
                          onChange={(e) => {
                            handleProductSearch(e.target.value);
                            setShowProductDropdown(true);
                            setHighlightedIndex(0);
                          }}
                          onKeyDown={handleProductKeyDown}
                          onFocus={() => {
                            if (productSuggestions.length > 0) {
                              setShowProductDropdown(true);
                            }
                          }}
                          placeholder="Search products by hsnsac, title..."
                          autoFocus
                        />
                        <button className="input-group-text ">
                          <i className="bx bx-search"></i>
                        </button>
                      </div>
                      {productSearchTerm && productSuggestions.length === 0 && (
                        <ul className="suggestions-list">
                          <li className="suggestion-item text-muted">
                            No products data was found
                          </li>
                        </ul>
                      )}
                      {showProductDropdown && productSuggestions.length > 0 && (
                        <ul className="suggestions-list">
                          {productSuggestions.map((product, index) => (
                            <li
                              ref={(el) => (suggestionRefs.current[index] = el)}
                              key={product._id}
                              onClick={() => handleSelectProduct(product)}
                              className={`suggestion-item ${
                                highlightedIndex === index ? "highlighted" : ""
                              }`}
                            >
                              <span className="badge rounded-pill bg-label-navy">
                                {product.code}
                              </span>{" "}
                              {/* -{" "} */}
                    {/* <span className="badge rounded-pill bg-label-retro">
                                                        {product.hsnsac}
                                                      </span>{" "} 
                              - {product.title} -{" "}
                              <span className="badge bg-label-fall rounded-pill">
                                ₹
                                {CurrencyConverter.toCurrency(
                                  product.saleprice || 0,
                                  "INR",
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div> */}
                    <div className="col-12">
                      <label className="form-label w-100">
                        From Date <span className="text-danger"> *</span>
                      </label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={dayjs(fromDate)}
                          maxDate={dayjs(serverTime)}
                          format="DD MMM YYYY"
                          onChange={handleFromDateChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              inputProps: { readOnly: true },
                              onKeyDown: (e) => e.preventDefault(),
                              sx: {
                                "& .MuiInputBase-input": {
                                  cursor: "pointer",
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="col-12">
                      <label className="form-label w-100">
                        To Date <span className="text-danger"> *</span>
                      </label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={dayjs(toDate)}
                          minDate={dayjs(toDateMin)}
                          maxDate={dayjs(toDateMax)}
                          format="DD MMM YYYY"
                          onChange={handleToDateChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              inputProps: { readOnly: true },
                              onKeyDown: (e) => e.preventDefault(),
                              sx: {
                                "& .MuiInputBase-input": {
                                  cursor: "pointer",
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>

                    {/* <div className="col-12">
                      <label className="form-label w-100">
                        Store <span className="text-danger"> *</span>
                      </label>
                      <Select
                        options={options}
                        onChange={handleSelectStore}
                        value={isSelectedStore}
                        ref={storeSelectedRef}
                        isSearchable
                        formatOptionLabel={renderOptionLabel}
                      />
                      {errors.store && (
                        <div className="invalid-feedback">{errors.store}</div>
                      )}
                    </div> */}

                    {/* <div className="col-12">
                      <label className="form-label w-100">Sale Mode</label>
                      <select
                        className="form-select"
                        value={saleMode}
                        onChange={(e) => setSaleMode(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="INVOICE">Tax Invoice</option>
                        <option value="BILL">Bill Receipt</option>
                      </select>
                      {errors.salemode && (
                        <div className="invalid-feedback">
                          {errors.salemode}
                        </div>
                      )}
                    </div> */}

                    <div className="col-12">
                      <div className="d-grid">
                        {isbtnLoading ? (
                          <button
                            className="btn btn-primary pb-0 text-right"
                            disabled
                          >
                            <CircularProgress
                              size={22}
                              className="text-white"
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary"
                            id="salesreportbtn"
                            onClick={handleGenerateReport}
                          >
                            Generate <ChevronRightIcon/>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-10">
              <div className="card p-3">
                <ViewServiceDoneByReport
                  isData={isProductReportData}
                  isPreLoading={isPreLoading}
                  isActive={isActive}
                  onSaleView={handleSaleView}
                />
              </div>
            </div>
          </div>
        </div>

        {!templateId && isViewTempModalOpen && (
          <InvoiceTempPreview
            isData={viewData}
            onClose={() => setViewTempModalOpen(false)}
            onAdd={handleSetTemplate}
          />
        )}
        {templateId && isViewModalOpen && (
         <TemplateModalLayout
          isOpen={isViewModalOpen}
          isData={viewData}
          onClose={handleSaleClose}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          setSaleData={setViewData}
          isActive={isActive}
        />  
        )}     
      </div>
    </div>
  );
};

export default ServiceDoneByReport;
